<!--suppress ALL -->
<template>
  <div class="chart-item">
    <div class="inner">
      <div v-if="datas.length > 0" :class="`chart-body ${isHorizontal && 'is-horizontal'}`">
        <div class="c-content">
          <div v-if="hasNoData" class="no-chart-viewed tooltip"
               :data-tooltip="$t('text.no_content_loaded')"></div>
          <pie-chart v-else :labels="labels" :datas="datasFormated" :options="defaultOptions"/>
        </div>
        <div v-for="(labelFormats, index) in labelsFormated" class="c-detail">
          <div v-for="(data, index) in labelFormats"
               :class="`detail ${data.hasData && 'tooltip'}`" :data-tooltip="data.label">
            <template v-if="data.hasData">
              <p class="title is-5">
                {{data.value}}
              </p>
              <p>{{data.label}}</p>
            </template>
          </div>
        </div>
      </div>
      <div v-if="$scopedSlots.default || $slots.default" class="btn-wrp">
        <slot/>
      </div>
      <div class="btn-wrp" v-if="button">
        <g-button type="success" @click="buttonClick" :is-loading="buttonLoading">
          {{buttonText}}
        </g-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {Component, Vue, Prop} from 'annotation';
  import PieChart from "./PieChart.vue";
  import {cloneDeep, isNil} from 'lodash';
  import GButton from "../common/button/GButton.vue";

  @Component({components: {GButton, PieChart}})
  export default class ChartItem extends Vue {

    @Prop({required: true})
    public title: string;

    @Prop({required: true})
    public labels: Array<string>;

    @Prop({default: {}})
    public options: Object;

    @Prop({required: true})
    public datas: Array<any>;

    @Prop({required: true})
    public isHorizontal: boolean;

    @Prop({required: true})
    public button: boolean;

    @Prop({required: true})
    public buttonLoading: boolean;

    @Prop({required: true})
    public buttonText: string;

    public buttonClick() {
      this.$emit('buttonClick');
    }

    public get defaultOptions() {
      return Object.assign({legend: {display: false}}, this.options);
    }

    public get hasNoData() {
      const {datas} = this;
      if (datas.length === 0) {
        return false;
      }

      return (datas[0].data || []).every((item: number) => item === 0);
    }

    public get datasFormated() {
      const {datas} = this;

      if (datas.length === 0) {
        return [];
      }

      return cloneDeep(datas).map((item) => {
        if (isNil(item.backgroundColor)) {
          item.backgroundColor = window.randomColor({
            count: datas[0].data.length,
            hue: 'red'
          });
        }

        return item;
      });
    }

    public get labelsFormated() {
      const {datas, labels, datasFormated} = this;
      let n = 3;
      let labelFormats = [];
      let labelFormated = [];
      if (datas.length === 0 || labels.length === 0) {
        return [];
      }

      (datas[0].data || []).forEach((item, index) => {
        labelFormats.push({
          value: item, label: labels[index], hasData: true,
          bgColor: datasFormated[0].backgroundColor[index]
        });
      });

      for (let i = 0, j = 0; i < labelFormats.length; i++) {
        if (i >= n && i % n === 0) j++;

        labelFormated[j] = labelFormated[j] || [];
        labelFormated[j].push(labelFormats[i])
      }

      if (labelFormated.some((item) => item.length < 3)) {
        const lastItem = labelFormated[labelFormated.length - 1];
        new Array(3 - lastItem.length).fill({
          value: '', label: '',
          hasData: false, bgColor: ''
        }).forEach((item: any) => {
          labelFormated[labelFormated.length - 1].push(item);
        });
      }

      return labelFormated;
    }
  }
</script>

<style lang="scss">
  @import "../../../style/variables_function";

  .chart-item {
    padding: 1rem;

    .chart-head, .chart-body {
      padding: .75rem;
    }

    .btn-wrp {
      margin: 1rem -1rem -1rem -1rem;
      padding: 1rem;
      background-color: $color-background;

      a {
        width: 100%;
      }
    }

    .chart-head {
      border-bottom: 1px solid $grey-lighter;
      margin: -1rem -1rem 1rem -1rem;
      display: flex;
      align-items: center;
      height: 73px;

      p {
        margin-bottom: 0;
        display: block;
        text-overflow: initial;
        white-space: normal;
      }
    }

    .chart-body {
      .c-content {
        margin: 0 auto 2rem auto;
        max-width: 250px;

        .no-chart-viewed {
          max-width: 250px;
          max-height: 250px;
          width: 100%;
          height: 100%;
          padding-bottom: 100%;
          border-radius: 50%;
          background-color: $grey-lighter;
        }

        &:only-child {
          .no-chart-viewed {
            padding-bottom: 0;
            height: 300px;
            width: 300px;
          }
        }
      }

      .c-detail {
        position: relative;
        overflow: inherit;
        @include fl_row;

        .detail {
          flex: 1;
          padding: .5rem;

          p {
            text-align: center;

            &:first-child {
              margin-bottom: .5rem;
            }

            &:last-child {
              color: $color-text-mute;
              font-size: .75rem;
            }
          }
        }
      }

      &.is-horizontal {
        @include fl_row;

        .no-chart-viewed {
          max-width: 400px;
          max-height: 400px;
          padding-bottom: 100%;
        }

        justify-content: center;
        align-items: center;

        .c-detail {
          flex: 1;
          @include fl_col;
          margin-left: 1rem;
          min-height: 100px;

          .detail {
            border-right: none;
            @include fl_row;
            padding: .5rem 0;
            width: 100%;
            align-items: center;

            p {
              font-size: 1.1rem;

              &:first-child {
                font-size: 2rem;
                margin-right: .5rem;
                margin-bottom: 0;
              }
            }

            &:not(:last-child) {
              border-bottom: 1px solid $grey-lighter;
            }
          }
        }

        .c-content {
          margin: 0;
          flex: 1;
          max-width: none;
        }
      }
    }
  }

</style>
